import { primaryButtonSurface } from '@/components/web-fe/design-v2/surfaces';
import { cn } from '@listening/utils/react-helpers';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { DocumentInsightsLoading } from './document-insights-loading';
import { useReadAlongSidebarStore } from './read-along-store';
import {
  type DocumentInsightsFailureKind,
  documentInsightsFailureKindForReason,
  documentInsightsSelection,
  savedDocumentInsightsKey,
} from '@listening/frontend/query/document-insights';
import { useSavedDocumentInsights } from '@listening/frontend/query/document-insights-hooks';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { Button } from '@listening/frontend/components/radix/button';
import { DocumentInsightsResult } from './document-insights-result';
import * as m from '@/paraglide/messages';

const documentInsightsErrorMessages: Record<
  DocumentInsightsFailureKind,
  () => string
> = {
  'no-text': m.read_along_ai_no_text_error,
  interrupted: m.read_along_ai_interrupted_error,
  generation: m.read_along_ai_generation_error,
  'rate-limited': m.try_magic_error_rate_limit,
  'not-ready': m.read_along_ai_not_ready_error,
  unavailable: m.read_along_ai_document_unavailable_error,
  'sign-in': m.read_along_ai_sign_in_error,
  connection: m.read_along_ai_connection_error,
};

const DocumentInsightsContext = createContext<
  (ReturnType<typeof useDocumentInsightsGeneration> & { title: string }) | null
>(null);

export function DocumentInsightsProvider({
  audioId,
  title,
  userId,
  enabled,
  children,
}: {
  title: string;
  audioId?: string | undefined;
  userId?: string | undefined;
  enabled: boolean;
  children: ReactNode;
}) {
  const generation = useDocumentInsightsGeneration(audioId, userId, enabled);
  return (
    <DocumentInsightsContext value={enabled ? { ...generation, title } : null}>
      {children}
    </DocumentInsightsContext>
  );
}

export function useDocumentInsights() {
  return useContext(DocumentInsightsContext);
}

function useDocumentInsightsGeneration(
  audioId: string | undefined,
  userId: string | undefined,
  enabled: boolean,
) {
  const { capture } = useAnalytics();
  const [openedFor, setOpenedFor] = useState<string>();
  const { sidebarState } = useReadAlongSidebarStore();
  const scope = savedDocumentInsightsKey(
    userId ?? '',
    audioId ?? '',
    documentInsightsSelection,
  ).join(':');
  const opened = openedFor === scope;
  const insights = useSavedDocumentInsights({
    audioId,
    userId,
    enabled: enabled && opened && sidebarState === 'information',
  });
  return {
    ...insights,
    opened,
    open: () => {
      setOpenedFor(scope);
      if (audioId)
        capture('document_insights_ui_interaction', {
          interaction: 'sidebar_opened',
          audio_id: audioId,
        });
    },
    generate: () => {
      if (!audioId || !insights.generate()) return;
      capture('document_insights_ui_interaction', {
        interaction: 'retry_clicked',
        audio_id: audioId,
      });
    },
  };
}

export function DocumentAiContent() {
  const generation = useDocumentInsights();
  if (!generation) return null;
  const { saved, loading, failureKind, generate } = generation;
  if (saved?.status !== 'completed' && failureKind) {
    return (
      <DocumentInsightsRetry
        message={documentInsightsErrorMessages[failureKind]()}
        onRetry={generate}
      />
    );
  }
  if (saved?.status === 'pending') {
    return (
      <DocumentInsightsLoading
        label={
          saved.progress?.phase === 'writing'
            ? m.read_along_ai_writing()
            : m.read_along_ai_preparing()
        }
      />
    );
  }
  if (!saved || (saved.status === 'failed' && loading)) {
    return <DocumentInsightsLoading label={m.mobile_loading()} />;
  }
  if (saved?.status === 'failed') {
    return (
      <DocumentInsightsRetry
        message={documentInsightsErrorMessages[
          documentInsightsFailureKindForReason(saved.error.reason)
        ]()}
        onRetry={generate}
      />
    );
  }
  const insights = saved?.status === 'completed' ? saved.result : undefined;
  return (
    <div className='min-h-0 flex-1 overflow-y-auto'>
      <header className='flex h-14 items-center px-6 pr-14'>
        <h2 className='truncate text-[15px] leading-[1.4] font-semibold text-[var(--v2-text-muted)]'>
          {m.read_along_ai_title()}
        </h2>
      </header>
      <DocumentInsightsResult
        title={generation.title}
        description={insights?.description}
        summary={insights?.summary}
        keyPoints={insights?.keyPoints}
      />
    </div>
  );
}

function DocumentInsightsRetry({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto p-6 text-center'>
      <p
        role='alert'
        className='max-w-64 text-sm leading-relaxed text-[var(--v2-text-muted)]'
      >
        {message}
      </p>
      <Button
        variant='ghost'
        size='sm'
        className={cn(
          'shrink-0 px-4 font-semibold hover:bg-[var(--v2-cta-bg)] hover:text-[var(--v2-cta-text)]',
          primaryButtonSurface,
        )}
        onClick={onRetry}
      >
        {m.common_try_again()}
      </Button>
    </div>
  );
}
